/**
 * API contracts shared by both dashboards.
 *
 * Shared pages/components depend ONLY on these interfaces — never on a concrete
 * axios instance or service file. Each app (local-node, cloud-node) provides an
 * implementation and injects it through <ApiProvider>. This is what lets the same
 * page work against the local backend (`/roles`) and the cloud backend
 * (which may node-scope or tunnel-wrap the same call) without any change to the page.
 *
 * Add one interface per domain as pages are extracted into the shared package.
 */

/** A permission string, e.g. "cameras.view". */
export type Permission = string;

export interface Role {
  id: string;
  name: string;
  description?: string;
  /** Permission ids/strings attached to the role. Shape kept loose across nodes. */
  permissions?: Permission[];
  permission_ids?: string[];
  is_system?: boolean;
  [key: string]: unknown;
}

export interface RoleInput {
  name: string;
  description?: string;
  permissionIds?: string[];
}

/** Roles domain — identical endpoints on both nodes (`/roles`). */
export interface RoleApi {
  list(): Promise<Role[]>;
  get(id: string): Promise<Role>;
  create(data: RoleInput): Promise<Role>;
  update(id: string, data: Partial<RoleInput>): Promise<Role>;
  delete(id: string): Promise<unknown>;
  /** Permissions payload shape varies by backend (flat list + grouped); kept loose. */
  getPermissions(): Promise<any>;
}

/** Pagination metadata (local node paginates users; cloud returns the full list). */
export interface PageMeta {
  page: number;
  totalPages: number;
  total: number;
  [key: string]: unknown;
}

/**
 * Canonical user shape used by shared user UI. Each node's adapter maps its backend
 * fields onto this (local `full_name`→`name`, `role_name`→`role`); shared components
 * never see the backend-specific names.
 */
export interface User {
  id: string;
  name: string;
  email: string;
  role?: string;
  role_id?: string | null;
  is_active?: boolean;
  created_at?: string;
  group_access?: unknown[];
  /** 'cloud' on a local node = provisioned from the cloud (read-only there, no login). */
  user_type?: string;
  [key: string]: unknown;
}

export interface UserListResult {
  data: User[];
  meta?: PageMeta | null;
}

/** Canonical payload the shared user form emits; adapters map it back to each backend. */
export interface UserInput {
  name: string;
  email: string;
  password?: string;
  role_id?: string;
  is_active?: boolean;
}

/** Users domain. Adapters normalize field names and (optionally) pagination. */
export interface UserApi {
  list(params?: { page?: number; limit?: number }): Promise<UserListResult>;
  get(id: string): Promise<User>;
  create(data: UserInput): Promise<unknown>;
  update(id: string, data: Partial<UserInput>): Promise<unknown>;
  delete(id: string): Promise<unknown>;
  /** Roles available to assign to a user, as `{ id, name }`. */
  listRoles(): Promise<Array<{ id: string; name: string; [key: string]: unknown }>>;
}

/**
 * The full set of API domains a host app must provide. Grows as more domains are
 * shared (cameras, groups, recordings, ...). Each new domain adds one property here
 * plus its interface above.
 */
/** A camera group. `list()` may return flat or nested; the page builds the tree. */
export interface Group {
  id: string;
  name: string;
  description?: string | null;
  parent_id?: string | null;
  children?: Group[];
  camera_count?: number;
  [key: string]: unknown;
}

export interface GroupInput {
  name: string;
  description?: string;
  parent_id?: string | null;
}

/**
 * Groups domain. The cloud adapter binds the selected node id (read from localStorage)
 * so node-scoping stays invisible to the shared page; local talks to its backend directly.
 */
export interface GroupApi {
  /** All groups (flat or already nested). The shared page normalizes into a tree. */
  list(): Promise<Group[]>;
  create(data: GroupInput): Promise<unknown>;
  update(id: string, data: Partial<GroupInput>): Promise<unknown>;
  delete(id: string): Promise<unknown>;
  /** Cameras in a group (recursive). */
  getCameras(groupId: string): Promise<any[]>;
}

/** An analytics/AI model. */
export interface Model {
  id: string;
  name: string;
  description?: string | null;
  status?: string;
  group_count?: number;
  camera_count?: number;
  [key: string]: unknown;
}

export interface ModelInput {
  name: string;
  description?: string;
}

/** A camera as needed by the model-attach picker. */
export interface CameraRef {
  id: string;
  name: string;
  [key: string]: unknown;
}

/** Models domain (cloud adapter binds the selected node id). */
export interface ModelApi {
  list(params?: { page?: number; limit?: number }): Promise<{ data: Model[]; meta?: PageMeta | null }>;
  create(data: ModelInput): Promise<unknown>;
  update(id: string, data: Partial<ModelInput>): Promise<unknown>;
  delete(id: string): Promise<unknown>;
  attachToCamera(modelId: string, cameraId: string, config: unknown): Promise<unknown>;
  /** Cameras available to attach a model to. */
  listCameras(): Promise<CameraRef[]>;
  /**
   * Report slugs whose events table holds data on this node (`['face', 'ocr', …]`). The nav shows
   * a report when its model is assigned OR listed here, so unassigning a model does not hide the
   * history it recorded. Optional: a host or node without it falls back to "assigned only".
   */
  surfacesWithData?(): Promise<string[]>;
}

/** A camera found during ONVIF discovery. */
export interface DiscoveredCamera {
  ip_address: string;
  device_name?: string;
  name?: string;
  manufacturer?: string;
  model?: string;
  firmware?: string;
  rtsp_urls?: string[];
  [key: string]: unknown;
}

export interface DiscoveryResults {
  data: DiscoveredCamera[];
  total: number;
}

export interface DiscoveryScanHandlers {
  /** Called with the full current result set as the scan progresses. */
  onResults: (cameras: DiscoveredCamera[], total: number) => void;
  /** Called once when the scan finishes (or fails). */
  onDone: (error?: string) => void;
}

/**
 * Discovery domain. The adapter owns the "how scan results arrive" mechanism — local
 * streams via socket.io, cloud polls a node-scoped endpoint — behind watchScan(), so the
 * shared page just renders results.
 */
export interface DiscoveryApi {
  startScan(subnet: string): Promise<void>;
  getResults(params: { page?: number; limit?: number; search?: string }): Promise<DiscoveryResults>;
  bulkApprove(cameras: DiscoveredCamera[]): Promise<any>;
  /** IPs of already-registered cameras, to mark discovered ones as added. */
  listExistingCameraIps(): Promise<string[]>;
  /** Begin receiving scan results; returns an unsubscribe/stop function. */
  watchScan(handlers: DiscoveryScanHandlers): () => void;
}

// ── Alert Hexagon ───────────────────────────────────────────────────────────

/** A camera as a hexagon: identity + site/group placement + live/recording state. */
export interface HexCamera {
  id: string;
  name: string;
  /** Live stream pipeline is up (running). */
  live: boolean;
  /** Continuous recording pipeline is running. */
  recording: boolean;
  /** Convenience: up if either live or recording is running. Drives the hex base colour
   *  (online vs offline); the two dots show the live/recording breakdown. */
  online: boolean;
  groupId: string | null;
  groupName: string | null;
  siteId: string;
  siteName: string | null;
}

/** Active-alert counts for one camera (drives the hex colour). */
export interface HexCounter {
  critical: number;
  warning: number;
  normal: number;
  total: number;
}

/** One page of active alerts for the docked panel's infinite-scroll list. */
export interface HexAlertPage {
  data: HexAlert[];
  page: number;
  totalPages: number;
  total: number;
}

/** A single alert, normalized across nodes (severity already mapped to 3 tiers). */
export interface HexAlert {
  /** Alert id — needed to drive lifecycle actions (ack/resolve) and to remove a
   *  specific alert from the feed when it's cleared. May be absent on legacy streams. */
  id?: string;
  cameraId: string;
  severity: 'critical' | 'warning' | 'normal';
  eventType?: string;
  alertName?: string;
  description?: string;
  snapshotUrl?: string | null;
  /** Source event id (denormalized). Lets a node lazy-fetch the alert's snapshot when it
   *  isn't a directly-loadable URL (cloud blob-proxies node images through the tunnel). */
  eventId?: string;
  timestamp?: number;
  /** Lifecycle status. The hexagon only shows alerts that still need attention
   *  ('new'); acknowledged/assigned/resolved/cancelled drop off the layout. */
  status?: string;
  /**
   * Bumped when the node reports this alert's snapshot is stored and resolvable
   * (`alert:preview`). Alerts arrive with no image at all — the node's fast lane broadcasts
   * before its storage lane has written the event row — so a card shows the live stream
   * first and the thumbnail only once this changes. AlertCard keys its preview effect on
   * this, which is what re-runs a `loadPreview` that would have been too early to succeed.
   */
  previewReady?: number;
}

/**
 * "This alert's snapshot is now available." Emitted per alert, after the fact.
 * `snapshotUrl` is set by hosts that can serve the image as a plain URL (local); cloud
 * leaves it null and lets `loadPreview` fetch the bytes through the tunnel instead.
 */
export interface HexAlertPreview {
  alertId?: string;
  cameraId?: string;
  snapshotUrl?: string | null;
}

/** Lifecycle transition for one alert (ack/assign/resolve/cancel/reopen). */
export interface HexAlertLifecycle {
  alertId?: string;
  cameraId: string;
  status: string;
  severity?: HexAlert['severity'];
}

/**
 * A node's demo-reset cycle: it wipes every AI event, alert and attendance row on a timer.
 *
 * The board shows this because the alternative reads as a fault — alerts an operator was
 * working through simply disappear, and nothing on screen accounts for it. Announced by the
 * node (heartbeated and re-sent the instant a wipe lands), so `nextResetAt` is the node's
 * own arithmetic and a client that misses a beat still converges on the truth.
 */
export interface DemoResetInfo {
  /** False on every normal deployment — the board shows nothing at all in that case. */
  enabled: boolean;
  /** Cycle length, as configured on the node (DEMO_RESET_INTERVAL_MINUTES). */
  intervalMinutes: number | null;
  /** Epoch ms of the next wipe, or null if the node hasn't said (no countdown then). */
  nextResetAt: number | null;
}

/**
 * The node's wire shape (snake_case, ISO timestamps) → DemoResetInfo. Lives here rather than
 * in each adapter because all three of them — local socket.io, cloud single-node, cloud
 * all-sites — receive the identical payload, and three private copies of "is the cycle on"
 * is how two boards would end up disagreeing about whether a site is a demo.
 *
 * `next_reset_at` is trusted as sent: it is the node's own timer, and recomputing it from an
 * interval plus the client's clock would drift by whatever the two clocks disagree on.
 */
export function toDemoResetInfo(raw: any): DemoResetInfo {
  const minutes = Number(raw?.interval_minutes);
  const next = raw?.next_reset_at ? new Date(raw.next_reset_at).getTime() : NaN;
  return {
    enabled: raw?.enabled === true,
    intervalMinutes: Number.isFinite(minutes) && minutes > 0 ? minutes : null,
    nextResetAt: Number.isFinite(next) ? next : null,
  };
}

export interface HexAlertHandlers {
  /** Initial snapshot AND (for pollers) each refresh: full counters + recent feed. */
  onSnapshot: (counters: Record<string, HexCounter>, recent: HexAlert[]) => void;
  /**
   * IS THE FEED ACTUALLY CONNECTED — pushed whenever that changes.
   *
   * A dashboard cannot tell a quiet night from a dead subscription: both are a still panel with
   * old timestamps on it, and only one of them means what is on screen is wrong. Nothing else in
   * this interface can answer it — `onSnapshot` firing says the feed WAS up, and its silence is
   * exactly the ambiguity. So the transport reports its own state: `true` on connect, `false` on
   * drop, for as long as the watch lives.
   *
   * What "connected" means is the transport's own business. A socket host reports the socket;
   * a POLLING host reports whether its last poll succeeded, which is the same claim at that
   * host's resolution. Omitted by hosts that genuinely cannot tell, and a consumer that gets
   * nothing shows no indicator rather than guessing — an indicator that is always green is
   * worse than none, because it is believed.
   */
  onConnection?: (connected: boolean) => void;
  /** A single live alert to merge (local socket stream; pollers may omit). */
  onAlert?: (alert: HexAlert) => void;
  /**
   * A previously-delivered alert's snapshot has finished storing and can now be shown.
   * The page patches the matching alert in place (live tile, card list and recent feed) so
   * its preview resolves without disturbing anything else on screen. Omitted by hosts whose
   * alerts already carry a usable image on arrival.
   */
  onAlertPreview?: (preview: HexAlertPreview) => void;
  /**
   * A live lifecycle change (ack/assign/resolve/cancel). The page removes the alert
   * from the feed and reverts the camera's hex colour when the new status is not 'new'.
   * Local pushes these off the backend `alert:lifecycle` socket; pollers may omit it
   * (their next onSnapshot already excludes cleared alerts).
   */
  onLifecycle?: (change: HexAlertLifecycle) => void;
  /**
   * The node's event store was WIPED — every alert the page is holding is gone (the demo
   * reset TRUNCATEs the event tables on a cycle). A wipe emits no per-alert lifecycle
   * events, so this is the only thing that can tell a board its accumulated counters, cards
   * and live tiles are about rows that no longer exist; without it they survived until a
   * page reload. The page drops all of it and refetches. The adapter follows this with a
   * fresh onSnapshot, so anything still genuinely active (another site, on the all-sites
   * board) comes straight back.
   */
  onCleared?: () => void;
  /**
   * The node's demo-reset cycle changed or was (re)announced — see DemoResetInfo. Fires on
   * connect (the host replays the last announcement), on each heartbeat, and right after a
   * wipe, which is what restarts the board's countdown from the node's clock rather than
   * from the client's guess at it.
   */
  onDemoSchedule?: (info: DemoResetInfo) => void;
  /**
   * Live STREAM running/offline updates (cameraId → running). Keeps the LIVE dot fresh
   * after mount. Fed by the `stream:started`/`stream:error` socket and the overall
   * `camera:status_changed` (streaming ⇒ live up). The page debounces the offline edge.
   */
  onLiveStatus?: (live: Record<string, boolean>) => void;
  /**
   * RECORDING running/offline updates (cameraId → running). Keeps the REC dot fresh.
   * Fed by `recording:started`/`recording:stopped` (is_recording) and the overall
   * `camera:status_changed`. The page debounces the offline edge.
   */
  onRecordingStatus?: (recording: Record<string, boolean>) => void;
}

/**
 * Alert Hexagon domain. The adapter owns the "how alerts arrive" mechanism — local
 * streams via socket.io (+ rollup snapshot), cloud polls a node-scoped endpoint —
 * behind watch(), so the shared page just renders cameras + colours + the alert feed.
 */
export interface HexAlertsApi {
  /** All cameras for this node/app, annotated with site/group + online state. */
  loadCameras(): Promise<HexCamera[]>;
  /** Begin receiving alert counters + recent feed; returns an unsubscribe/stop function. */
  watch(handlers: HexAlertHandlers): () => void;
  /** Paginated list of ACTIVE alerts (status 'new'), latest-first — backs the docked
   *  panel's infinite-scroll card list. Optional: a node that doesn't support it falls
   *  back to the realtime `watch()` feed only (no scroll-to-load). */
  listActiveAlerts?(params: { page: number; limit: number }): Promise<HexAlertPage>;
  /** Lazily resolve a preview image (object URL / data URL) for an alert whose snapshot
   *  isn't a directly-loadable URL — e.g. cloud, which blob-fetches node images through the
   *  tunnel. Returns null when unavailable. Hosts exposing `snapshotUrl` directly omit it.
   *  The caller owns revoking any returned object URL. */
  loadPreview?(alert: HexAlert): Promise<string | null>;
  /** Acknowledge an alert (operator is now on it). Optional: hosts that support the
   *  lifecycle implement it; the alert tiles only render the action when present. */
  acknowledgeAlert?(alertId: string): Promise<void>;
  /** Resolve an alert (terminal). */
  resolveAlert?(alertId: string, note?: string): Promise<void>;
}

// ── Map View (floor-plan layouts) ───────────────────────────────────────────

/** A floor plan / site plan, without its image bytes. */
export interface MapLayoutSummary {
  id: string;
  name: string;
  description?: string | null;
  image_mime: string;
  /** Natural pixel size of the plan. The canvas uses it as its content box and
   *  placements are fractions of it, so pins survive zoom and re-upload. */
  image_width: number;
  image_height: number;
  /** The layout the page opens on. Exactly one layout carries it. */
  is_default: boolean;
  sort_order: number;
  /** Cameras pinned on this layout that the caller can see. */
  camera_count: number;
  updated_at?: string;
  [key: string]: unknown;
}

/**
 * One camera pinned on a layout. `x`/`y` are fractions of the image (0..1).
 *
 * The install fields are surveyed detail an operator adds from the pin's dialog and reads
 * back on hover. `mount_height_m` / `tilt_deg` are nullable on purpose: "not surveyed" is a
 * real state, and defaulting them to 0 would claim a camera sits on the floor aimed level.
 */
export interface MapPlacement {
  camera_id: string;
  x: number;
  y: number;
  /** Where it POINTS: degrees clockwise from north. Drives the pin's direction notch and
   *  the field-of-view cone. */
  rotation?: number;
  /** How WIDE it sees, in degrees — the cone's spread. */
  fov_deg?: number;
  /** Mounting height in metres, or null when not surveyed. */
  mount_height_m?: number | null;
  /** Downward tilt from horizontal in degrees, or null when not surveyed. */
  tilt_deg?: number | null;
  /** Free-text placement note — "above door 3, north-east corner". */
  location_note?: string | null;
  label?: string | null;
  /** Denormalized from the camera row so a pin can be drawn before alert traffic arrives. */
  camera_name?: string;
  camera_status?: string;
}

export interface MapLayoutDetail extends MapLayoutSummary {
  placements: MapPlacement[];
}

/** Where a camera is already pinned — powers the editor's "already on Floor 1" state. */
export interface MapPlacedCamera {
  layout_id: string;
  layout_name: string;
}

export interface MapLayoutInput {
  name: string;
  description?: string;
  imageMime: string;
  /** base64, no `data:` prefix. */
  imageData: string;
  imageWidth: number;
  imageHeight: number;
}

/**
 * Map View domain — floor-plan layouts and the camera pins on them.
 *
 * Node-scoping lives in the adapter, like GroupApi: cloud binds the selected node and
 * tunnels each call; local talks to its backend directly.
 *
 * `imageUrl` is a METHOD rather than a field because the two nodes resolve the plan
 * differently — local returns a plain `/api/map-layouts/:id/image` URL, cloud returns a
 * `data:` URL built from base64 carried over the tunnel (the same split
 * `AttendanceApi.eventImageDataUrl` uses). The page just sets it as `<img src>`.
 */
export interface MapLayoutApi {
  list(): Promise<MapLayoutSummary[]>;
  get(id: string): Promise<MapLayoutDetail>;
  imageUrl(layout: MapLayoutSummary): Promise<string>;
  create(input: MapLayoutInput): Promise<MapLayoutSummary>;
  update(id: string, patch: Partial<MapLayoutInput> & { is_default?: boolean }): Promise<MapLayoutSummary>;
  delete(id: string): Promise<void>;
  /** cameraId → the layout that owns it, across ALL layouts. */
  placedCameras(): Promise<Record<string, MapPlacedCamera>>;
  /**
   * Replace a layout's whole placement set. Rejects with a `MapPlacementConflict` when a
   * camera is already pinned on another layout — a camera belongs to exactly one layout,
   * enforced by a unique constraint on the node's database.
   */
  setPlacements(id: string, placements: MapPlacement[]): Promise<MapLayoutDetail>;
}

/** One camera that blocked a placement save (the 409 body's `error.details`). */
export interface MapPlacementConflict {
  camera_id: string;
  camera_name: string;
  layout_id: string;
  layout_name: string;
}

// ── Map View (geographic) ───────────────────────────────────────────────────

/**
 * One camera placed on the geographic map.
 *
 * Unlike a floor-plan placement these are COLUMNS ON THE CAMERA, not rows in a placements
 * table — there is one Earth, so a latitude/longitude needs no layout to be meaningful (see
 * the node's migration 031). A camera may carry both a plan pin and coordinates; the two
 * boards answer different questions and neither placement invalidates the other.
 */
export interface GeoPlacement {
  camera_id: string;
  /** WGS84 degrees. */
  lat: number;
  lng: number;
  /** Where the camera POINTS: degrees clockwise from north — the same convention the
   *  floor-plan placements use, so one marker component draws either board's pin.
   *  Null means "not surveyed", which the marker draws differently from a deliberate 0°. */
  rotation?: number | null;
  /** How WIDE it sees, in degrees. Null = unset; the board falls back to 90°. */
  fov_deg?: number | null;
  /** Denormalized from the camera row, so a pin can be drawn and labelled before any alert
   *  traffic arrives. `camera_status` is the authoritative connectivity signal. */
  camera_name?: string;
  camera_status?: string;
  camera_location?: string | null;
}

/**
 * The deployment's saved default view, plus the basemap to draw it on.
 *
 * NO TILES SHIP WITH THE PRODUCT — bundling a tile pack would add hundreds of megabytes to
 * every deployment for a feature many sites never open. `tile_url` therefore defaults to the
 * public OpenStreetMap service, which means the board needs internet reachable FROM THE
 * BROWSER; it probes for that and says so plainly rather than drawing an empty grid. A site
 * without internet points this at a tile server on its own network.
 */
export interface GeoMapConfig {
  center_lat: number;
  center_lng: number;
  zoom: number;
  tile_url: string;
  tile_attribution: string;
  /**
   * Where the board's search box resolves a PLACE NAME. `{q}` is the URL-encoded query.
   *
   * Same bargain as `tile_url`: a public default (Nominatim) so search works out of the box,
   * replaceable because that endpoint may be unreachable or unsuitable. Searching by
   * COORDINATES is parsed in the browser and needs none of this.
   */
  geocoder_url: string;
  /** False until someone saves a default — lets the board say "no default location set"
   *  instead of presenting the fallback world view as a deliberate choice. */
  is_configured: boolean;
  updated_at?: string | null;
}

/** What the editor submits: a DELTA, never the whole board. A scoped operator submitting
 *  their own cameras must not be able to wipe coordinates they cannot see. */
export interface GeoPlacementSave {
  placed?: Array<Pick<GeoPlacement, 'camera_id' | 'lat' | 'lng' | 'rotation' | 'fov_deg'>>;
  cleared?: string[];
}

/**
 * Map View domain — camera coordinates and the deployment's default map view.
 *
 * Node-scoped in the adapter, like MapLayoutApi: cloud binds the selected node and tunnels
 * each call; local talks to its backend directly.
 */
export interface GeoMapApi {
  config(): Promise<GeoMapConfig>;
  /** Save the default location, zoom and (optionally) the basemap. Needs `map.edit`. */
  saveConfig(
    input: Pick<GeoMapConfig, 'center_lat' | 'center_lng' | 'zoom'> &
      Partial<Pick<GeoMapConfig, 'tile_url' | 'tile_attribution' | 'geocoder_url'>>,
  ): Promise<GeoMapConfig>;
  placements(): Promise<GeoPlacement[]>;
  /** Apply the editor's changes; resolves with the placement list as it now stands. */
  savePlacements(input: GeoPlacementSave): Promise<GeoPlacement[]>;
}

// ── Access Control ──────────────────────────────────────────────────────────

/** A user's camera access = cameras from these groups ∪ these individual cameras. */
export interface AccessSelection {
  groupIds: string[];
  cameraIds: string[];
}

/**
 * Access Control domain — assign camera GROUPS + individual CAMERAS to a user.
 *
 * Node-scoping is handled inside the adapter (cloud binds the globally-selected
 * node id, mirroring GroupApi/ModelApi); the shared page stays node-unaware and
 * refetches when its `nodeKey` prop changes. Local talks to its backend directly.
 */
export interface AccessControlApi {
  /** Users manageable here (cloud: cloud users with a grant on the selected node). */
  listUsers(): Promise<User[]>;
  /** All groups for the (selected) node — flat or nested; the page trees it. */
  listGroups(): Promise<Group[]>;
  /** All cameras for the (selected) node. */
  listCameras(): Promise<CameraRef[]>;
  /** Cameras in a group, recursive (incl. sub-groups). */
  groupCameras(groupId: string): Promise<CameraRef[]>;
  /** A user's current selection. */
  getAccess(userId: string): Promise<AccessSelection>;
  /** Replace a user's selection. */
  setAccess(userId: string, selection: AccessSelection): Promise<void>;
}

// ── Attendance ──────────────────────────────────────────────────────────────

/** One enrollment photo attached to an employee. */
export interface EmployeePhoto {
  id: string;
  /** 'unknown_event' when the photo came from linking an unknown face event. */
  source?: string;
  [key: string]: unknown;
}

/**
 * Filters for the unknown-face feed.
 *
 * `from` / `to` are UNIX epoch SECONDS, matching the `event_timestamp` column the
 * command-center compares them against — not milliseconds and not an ISO string, either
 * of which silently matches nothing rather than erroring.
 *
 * The window is applied in SQL, not in the browser: the timeline's repair flow asks for a
 * few minutes around a missed sighting, and narrowing client-side would mean paging the
 * whole feed across the tunnel just to throw almost all of it away — and would miss
 * candidates entirely whenever the window fell outside the fetched page.
 */
export interface UnknownFaceQuery {
  limit?: number;
  page?: number;
  /** Inclusive lower bound, epoch seconds. */
  from?: number;
  /** Inclusive upper bound, epoch seconds. */
  to?: number;
}

/**
 * Attendance domain — employees, presence, in/out timeline, enrolment photos and
 * per-camera entry/exit roles. Payloads stay `any`: the command-center returns
 * wide, evolving row shapes and the pages render them defensively.
 *
 * Node-scoping lives in the adapter, like GroupApi: cloud binds the selected node
 * and peels its double envelope (cloud BE wrapper + CC wrapper); local talks to
 * the command-center directly through nginx. Pages take a `nodeKey` prop purely
 * so they refetch when the operator switches node on cloud.
 */
export interface AttendanceApi {
  // Module config
  getConfig(): Promise<any>;
  updateConfig(patch: any): Promise<any>;

  // Presence + timeline
  today(date?: string): Promise<any>;
  timeline(params?: { date?: string; camera_id?: string; search?: string }): Promise<any>;

  // Employees
  listEmployees(params?: any): Promise<any[]>;
  getEmployee(id: string): Promise<any>;
  createEmployee(data: any): Promise<any>;
  updateEmployee(id: string, data: any): Promise<any>;
  /**
   * Remove an employee. Cascades their whole attendance record — days, sessions,
   * sightings, payroll adjustments and enrolment photos — and tells the AI module
   * to drop the identity's face vectors. Not reversible.
   */
  deleteEmployee(id: string): Promise<any>;
  employeeDay(id: string, date?: string): Promise<any>;

  // ── The registry, and who in it is staff ──
  //
  // An employee IS a registered person with an employment record attached (the command-center's
  // `persons` is the master registry). These three make that reversible from the attendance
  // module, which is where somebody decides whether a face the estate already recognises
  // belongs to staff.

  /**
   * Registered people. `is_employee: false` is the interesting query — faces the system
   * recognises that nobody has classified yet.
   */
  listRegisteredPeople(params?: { is_employee?: boolean; search?: string; limit?: number }): Promise<any[]>;
  /**
   * Attach an employment record to a registered person. Does NOT re-enrol: the AI module keys
   * vectors by person_id and this does not change it.
   */
  markPersonAsEmployee(personId: string, data?: any): Promise<any>;
  /**
   * Remove the employment record, keeping the person and their vectors. Rejects with 409 when
   * attendance history exists — pass `{ discard_attendance: true }` to confirm.
   */
  unmarkPersonAsEmployee(personId: string, data?: any): Promise<any>;

  employeeMonthly(id: string, month?: string): Promise<any>;
  /** Raw sightings — returns the paginated envelope `{ data, pagination }`. */
  employeeSightings(id: string, params?: any): Promise<any>;

  // Payroll — org-wide monthly present/absent summary (`month` = YYYY-MM, default current)
  monthlyReport(month?: string): Promise<any>;
  /**
   * Set an employee's hand-entered day counts for a month — leave, sick leave, WFH and days the
   * recognition model missed them. Accepts a subset of the fields (one edited cell) and returns
   * the recomputed `payable_days`.
   *
   * Holiday days are NOT here: they are derived from the org-wide holiday calendar, so the
   * column is read-only in the sheet.
   */
  setPayrollAdjustments(
    month: string,
    employeeId: string,
    patch: {
      unpaid_leave_days?: number;
      paid_leave_days?: number;
      sick_leave_days?: number;
      wfh_days?: number;
      ai_error_days?: number;
      note?: string;
    },
  ): Promise<any>;
  /**
   * Set the same day count on EVERY active employee for a month — the "AI model error for
   * everyone" case, when a camera or the ingest pipeline was down for a day. Overwrites, so
   * saving twice does not pay twice; per-employee edits afterwards behave as normal.
   */
  setPayrollBulkAdjustments(
    month: string,
    patch: {
      unpaid_leave_days?: number;
      paid_leave_days?: number;
      sick_leave_days?: number;
      wfh_days?: number;
      ai_error_days?: number;
    },
  ): Promise<any>;
  /**
   * Declare an org-wide holiday, or rename one already declared on that date. `date` is
   * YYYY-MM-DD. Which employees it earns a payable day for is decided by the server from the
   * date — a Sunday, a day they worked through, or a day before they joined earns nothing.
   */
  addPayrollHoliday(date: string, name: string): Promise<any>;
  /** Withdraw an org-wide holiday. `date` is YYYY-MM-DD. */
  removePayrollHoliday(date: string): Promise<any>;
  /** Override the month's working-day count; `null` clears it back to calendar-minus-Sundays. */
  setPayrollWorkingDays(month: string, workingDays: number | null): Promise<any>;
  /**
   * Payroll Setup: which day types earn a payable day (`count_*` booleans), how much of a day
   * the arguable ones are worth (`weight_*`, a whole or half day), and how long a day must be
   * to count as full or half (`full_day_minutes` / `half_day_minutes`). Accepts any subset.
   * Policy — applies to every month, past ones included.
   */
  setPayrollSettings(settings: Record<string, boolean | number>): Promise<any>;

  // Enrolment photos
  uploadEmployeePhotos(id: string, files: File[]): Promise<any>;
  removeEmployeePhoto(employeeId: string, photoId: string): Promise<any>;
  /** `data:` URL for an enrolment photo, or null when it can't be fetched. */
  employeePhotoDataUrl(photoId: string): Promise<string | null>;

  // Unknown faces → register / link
  listUnknownFaceEvents(params?: UnknownFaceQuery): Promise<any[]>;
  createEmployeeFromEvent(data: any): Promise<any>;
  linkEventToEmployee(employeeId: string, eventId: string): Promise<any>;
  /** `data:` URL for a face event image (`faceCrop` | `alignedFace`). */
  eventImageDataUrl(eventId: string, type?: string): Promise<string | null>;

  // Camera entry/exit roles
  listCameraConfigs(): Promise<any[]>;
  upsertCameraConfig(cameraId: string, data: any): Promise<any>;
  /** The node's cameras — for the timeline filter and the config table. */
  listCameras(): Promise<CameraRef[]>;
}

/**
 * One camera's heatmap coverage over a time range, as `/heatmap/cameras` returns it.
 *
 * Identity is only `camera_id`: heatmap metrics live in the command-center's database and
 * cameras live in the backend's, so there is no join to make server-side. The report page
 * stitches these onto its camera list by id.
 */
export interface HeatmapCameraSummary {
  camera_id: string;
  /** Records received in the range — one per reporting interval. */
  entries: number;
  total_hits: number;
  total_interval_seconds: number;
  /** The observed reporting interval; null when the module never sent one. */
  avg_interval_seconds: number | null;
  first_event_at: string | null;
  last_event_at: string | null;
  grid_rows: number | null;
  grid_cols: number | null;
  cell_size_px: number | null;
}

/**
 * ONE camera's heatmap over a time range: the coverage figures plus the spatial grid the
 * overlay is drawn from. Summed server-side across every record in the range.
 */
export interface HeatmapAggregate {
  /** rows × cols of hit counts. Empty or missing when nothing was reported. */
  spatial_grid: number[][] | null;
  grid_rows: number;
  grid_cols: number;
  total_hits: number;
  total_interval_seconds: number;
  /** Records that went into the sum — one per reporting interval. */
  event_count: number;
}

/**
 * Heatmap domain — the person-activity grids the AI module reports per camera.
 *
 * The live PLAYER the grid is drawn over is still app-local (ULLFU on the local node, the
 * tunnelled player in the cloud) and reaches the page as a render slot — but the reads are all
 * here, because the page that picks a camera and the page that draws its grid are now one page.
 */
export interface HeatmapApi {
  /** Per-camera coverage. `from`/`to` are epoch ms; omit for all history. */
  cameraSummary(params?: { from?: number; to?: number }): Promise<HeatmapCameraSummary[]>;
  /** One camera's summed grid over a range. `from`/`to` are epoch ms. */
  aggregated(cameraId: string, from: number, to: number): Promise<HeatmapAggregate | null>;
  /** The node's cameras, so cameras with no heatmap data can still be listed. */
  listCameras(): Promise<CameraRef[]>;
  /**
   * Camera ids the heatmap model is attached to, direct or group-inherited (see
   * `cameraIdsForModel`). Optional: where an app can't resolve it the report says a camera has
   * no data, rather than claiming the model is missing.
   */
  heatmapCameraIds?(): Promise<string[]>;
}

/**
 * Structured event / attribute search.
 *
 * The DSL mirrors the command-center's validator exactly. Note what is ABSENT: there is no
 * camera allow-list field. The caller never sends one — each node's backend resolves the
 * requester's accessible cameras from the RBAC database and staples it on server-side, and
 * overwrites anything a client tried to send. A UI that could name its own scope would be a UI
 * that could widen it.
 */
/**
 * One reading of a typed word — an attribute value, or words to look up in names, plates and OCR
 * text. See `SearchMatchGroup`.
 */
export type SearchMatchReading =
  | { attr: string; values: string[] }
  | { text: string; fields?: SearchTextField[]; fuzziness?: SearchFuzziness; whole_word?: boolean };

/**
 * One typed WORD and everything it could mean. A row must satisfy every word, each by any of its
 * readings, on the same row — "red truck" is a red truck or a sign reading both, never a person in
 * red near a truck. Send only when the schema advertises `match`.
 */
export interface SearchMatchGroup {
  word: string;
  any: SearchMatchReading[];
}

/**
 * An EVENT TYPE the one-box page lists and lets the operator remove: a model key, with OCR split
 * into `ocr_plate` (number plates it read) and `ocr_text` (the rest of its words).
 */
export type SearchEventType = string;

/** How the server read one word: each reading, and for a text reading the spellings it used. */
export interface SearchMatchReport {
  word: string | null;
  readings: Array<
    | { type: 'attr'; field: string; values: string[] }
    | { type: 'text'; q: string; fields: SearchTextField[];
        spellings: Array<{ term: string; events: number; kind: string; fields: SearchTextField[] }>;
        /** Registered people the word names — found by person id even where no event has the name. */
        people?: string[];
        unmatched: boolean }
  >;
}

/** `POST /search/refine` — what the current results contain, counted per refinement value. */
export interface SearchRefine {
  /** Per event type (`SearchEventType`), counted even when removed — so it can be put back. */
  types: SearchFacetValue[];
  cameras: SearchFacetValue[];
  /** `listed` / `not_listed`. */
  watchlist: SearchFacetValue[];
  attributes: Record<string, SearchFacetValue[]>;
  /** The counts took too long and were skipped; the results themselves are unaffected. */
  omitted?: boolean;
  camera_scope?: number;
  took_ms?: number;
}

export interface SearchDsl {
  time?: { preset?: string } | { from: string; to: string };
  cameras?: string[];
  groups?: string[];
  zones?: string[];
  models?: string[];
  sub_types?: string[];
  person?: { name?: string; person_id?: string };
  /** Vehicle plate, prefix-matched. Canonicalised server-side (uppercase, separators stripped). */
  plate?: string;
  /** The one-box search — see `SearchMatchGroup`. */
  match?: SearchMatchGroup[];
  /** Event types removed from the results. */
  hide_types?: SearchEventType[];
  /** field → one value or several. Several values for one field mean "any of". */
  attributes?: Record<string, string | string[]>;
  known?: boolean;
  /**
   * OCR rows only: `plate` = number reads, `text_only` = frames where no number was read.
   * Rows of other models are untouched. Send only when the schema advertises `ocr_read`.
   */
  ocr_read?: 'plate' | 'text_only';
  /**
   * Only subjects on a watch list, or only those off it.
   *
   * Answered against the CURRENT list rather than a verdict recorded on the event: somebody is
   * flagged because of something, and the question that follows is where they have been —
   * including before anyone flagged them. Categories narrow which listing counts and are only
   * valid alongside `listed: true`; the server rejects the other pairing rather than widening
   * the question.
   */
  watchlist?: { listed: boolean; categories?: string[] };
  min_confidence?: number;
  min_attr_confidence?: number;
  /**
   * The ANSWER SHAPE. The first four describe the matching rows; the last three answer questions
   * a row list cannot — "which areas", "the trail from entry to exit", "did all of them wear one".
   */
  aggregate?: SearchAggregate;
  /**
   * What counts as a place, for `by_place` and `journey`. Rejected on any other aggregate rather
   * than ignored, and `journey` refuses `'group'` — see `SearchSchema.place_aggregates`.
   */
  place_by?: SearchPlaceBy;
  /**
   * The attribute value `attribute_split` divides the matching set by. Required by that
   * aggregate, and rejected when `attributes` already filters the same field (every event would
   * land on one side of the split).
   */
  split_by?: { field: string; value: string };
  /**
   * Fuzzy text clauses — names, plates and words the OCR model read. Every clause must match
   * (AND); inside one, every word must match one of its accepted spellings. Only send this to a
   * node whose schema advertises `text_search`: an older node rejects the field with a 400.
   */
  text?: SearchTextClause[];
  /**
   * Words to ALSO find in OCR text, OR'd with everything else — "person in red shirt" also returns
   * a board that reads "red shirt". `list` only (ignored by other aggregates). Only send to a node
   * whose schema advertises `or_text`.
   */
  or_text?: string;
  /** Row order: `relevance` (default with text) or `newest`. Track mode always asks for newest. */
  sort?: SearchSort;
  limit?: number;
  offset?: number;
}

export type SearchTextField = 'name' | 'plate' | 'text';
export type SearchFuzziness = 'exact' | 'close' | 'loose';
export type SearchSort = 'newest' | 'relevance';

export interface SearchTextClause {
  q: string;
  /** Where to look. Default: all three. */
  fields?: SearchTextField[];
  /** exact = exact or prefix; close ≈ one mistake; loose ≈ two ("badal" finds "vadal", "kajal"). */
  fuzziness?: SearchFuzziness;
  /** Spellings the operator struck out — a bare word, or "field:word" for one field only. */
  exclude?: string[];
}

/** One stored spelling a typed word matched, with how many results it brings in scope. */
export interface SearchTextMatch {
  term: string;
  /** Where it was found — a word read as a name AND on a sign is one entry. */
  fields: SearchTextField[];
  kind: 'exact' | 'prefix' | 'fuzzy';
  distance: number;
  score: number;
  events: number;
  /** Struck out by the operator: listed so it can be put back, but no longer matching. */
  excluded: boolean;
}

/** How the server read one text clause — every spelling it used, so nothing matches silently. */
export interface SearchTextReport {
  q: string;
  fields: SearchTextField[];
  fuzziness: SearchFuzziness;
  dictionary_size: number;
  tokens: Array<{
    token: string;
    fields: SearchTextField[];
    matches: SearchTextMatch[];
    matches_total: number;
    /** This word matched nothing in scope — it is why the answer is empty. */
    unmatched: boolean;
  }>;
}

export type SearchAggregate =
  | 'count' | 'list' | 'distinct_persons' | 'timeline'
  | 'by_place' | 'journey' | 'attribute_split';

export type SearchPlaceBy = 'group' | 'camera' | 'zone';

/**
 * ONE DETECTED SUBJECT — one person, or one vehicle. Not one event.
 *
 * A frame holding six people is six of these rows, sharing an `event_id` and differing in
 * `subject_seq`. That is what makes an appearance filter mean what it says: "red top AND black
 * trousers" holds for ONE person here, where a per-frame row carried the union of everyone in it
 * and matched a person who was never in the picture.
 *
 * The practical consequence for a reader: `event_id` is NOT a unique key for a row. Use
 * `${event_id}:${subject_seq}` wherever one is needed, and read a result count as "detections",
 * with `distinct_persons` as the count of people.
 *
 * `attributes` is flat — no nesting — but a value is a string OR an array of strings. PERSON
 * fields are arrays ({ upper_color: ['red'] }) because `accessories` genuinely holds several
 * items for one person and one shape per subject beats a scalar saved. VEHICLE fields are
 * scalars ({ vehicle_type: 'car', plate: 'MH12AB1234' }).
 */
export interface SearchEventRow {
  event_id: string;
  source_table: string;
  model_key: string;
  camera_id: string;
  sub_type: string | null;
  zone: string | null;
  event_timestamp: string;
  work_date: string;
  /** 'person' | 'vehicle' | null — what this event is about. Null for fire, falls, counts. */
  subject_kind: string | null;
  /** The subject's canonical identity: a person id, a number plate, or a synthetic track key. */
  subject_id: string | null;
  /** What to show a human — a person's name or a plate. Null when unidentified. */
  subject_label: string | null;
  is_known: boolean;
  track_id: number | null;
  severity: string | null;
  confidence: number | null;
  attributes: Record<string, string | string[]>;
  attr_conf: Record<string, number>;
  image_urls: Record<string, string>;
  /**
   * Which subject of the event this row is — the module's own index for it, so it lines up with
   * the crop numbering. Absent on a node that predates the per-subject projection, where every
   * event is one row; treat a missing value as 0.
   */
  subject_seq?: number;
  /**
   * The key inside `image_urls` holding THIS subject's own crop, e.g. `person_crop_2`.
   *
   * A key rather than a URL, because the two hosts need different things from it: the local page
   * indexes `image_urls` with it, and the cloud page passes it to the tunnel as an image type
   * (node object-store URLs are not reachable from the cloud). Null where the module sent no
   * per-subject crop, and the caller falls back to the full frame.
   */
  crop_key?: string | null;
  /**
   * What the row SAYS: the words an OCR frame read ("Dethali Char Rasta | …"), or the raw read of
   * an OCR plate ("GJ182 / T3877"). Null on every other model.
   */
  text_content?: string | null;
  /** How well this row matched the text filter (higher is better). Present only with text. */
  text_score?: number;
  [key: string]: unknown;
}

/**
 * One place, for `aggregate: 'by_place'` — the "which areas did they visit" answer.
 *
 * `place` is an ID, not a label: a camera or group UUID, or a zone name. Names are resolved by
 * the page from the camera and group lists it already loads, so the API never has to hold a
 * second copy of them.
 */
export interface SearchPlaceRow {
  place: string;
  /** True for the "(no zone)" / "(no group)" bucket — flagged, so nothing has to parse the label. */
  unassigned?: boolean;
  events: number;
  /** Distinct subjects at this place. Null when `subjects_omitted` is set on the result. */
  subjects: number;
  first_seen: string;
  last_seen: string;
  /** Cameras rolled into this row. Group rows only. */
  cameras?: number;
  /**
   * The latest matching frame at this place — camera rows only.
   *
   * A partial SearchEventRow rather than a bare URL, because the page's `imageFor` /
   * `loadImage` resolvers take one of those: the local host builds a direct URL from it, the
   * cloud host streams it through the tunnel, and neither needs a second image path for this.
   *
   * Null when the place's matching events carried no image, or when their snapshots have aged
   * out of retention — the row still counts them, it just cannot show one.
   */
  sample?: Partial<SearchEventRow> | null;
}

/** One stop on a subject's trail. `dwell_seconds` is 0 for a single sighting, not an estimate. */
export interface SearchJourneyStop {
  place: string;
  unassigned?: boolean;
  events: number;
  first_seen: string;
  last_seen: string;
  dwell_seconds: number;
  /**
   * The stop's LEAD detection — its earliest — so the stop can be drawn as the frame it
   * actually was, and opened as a record.
   *
   * A stop was four numbers and a place name, which is a route nobody can recognise anybody in:
   * the operator had to leave the trail to find out whether the person at Reception was the
   * person they were following. Null on a node that predates these fields, which is why every
   * reader has to tolerate their absence rather than assume a frame.
   */
  event_id?: string | null;
  model_key?: string | null;
  image_urls?: Record<string, string> | null;
}

/** One subject's ordered trail, for `aggregate: 'journey'`. */
export interface SearchJourneyRow {
  subject: string;
  subject_label: string | null;
  subject_kind: string | null;
  is_known: boolean;
  events: number;
  /** In time order. The first is the entry and the last the exit WITHIN THE WINDOW. */
  stops: SearchJourneyStop[];
  first_seen: string | null;
  last_seen: string | null;
}

/** One subject on the "without" side of an `attribute_split`. */
export interface SearchSplitSubjectRow {
  subject: string;
  subject_label: string | null;
  is_known: boolean;
  events: number;
  first_seen: string;
  last_seen: string;
}

export interface SearchResult {
  success: boolean;
  /** Matching event count. Absent for the distinct_persons aggregate. */
  events?: number;
  /** Null when the server skipped it — see `distinct_omitted`. */
  distinct_persons?: number | null;
  /**
   * True when the distinct-subject count was too expensive for this window and was NOT computed.
   * The UI must say so rather than render a blank: an absent figure is honest, a blank looks
   * like zero.
   */
  distinct_omitted?: boolean;
  /**
   * The payload, whose SHAPE follows the aggregate:
   *   list             → SearchEventRow[]
   *   distinct_persons → one row per subject
   *   timeline         → { bucket, events, distinct_persons }[]
   *   by_place         → SearchPlaceRow[]
   *   journey          → SearchJourneyRow[]
   *   attribute_split  → SearchSplitSubjectRow[] (the WITHOUT side, capped)
   */
  rows?: SearchEventRow[] | SearchPlaceRow[] | SearchJourneyRow[] | SearchSplitSubjectRow[] | any[];
  /** How many cameras the query actually ran against, AFTER the RBAC intersection. */
  camera_scope?: number;
  /**
   * journey only — how many followable subjects MATCH, against the page of them in `rows`.
   *
   * `rows` is one page (twenty, because a trail expands into a whole route), so without this
   * the answer could not tell "these are all of them" from "these are the first twenty of three
   * hundred" — and read as tracking quietly losing results.
   */
  subjects_total?: number;
  /**
   * journey only — matching detections with NO subject, which no trail can ever include.
   *
   * A detection the model could not give a track or an identity to has no subject, so it matches
   * a filter in Search and cannot be followed here. Counted rather than omitted: the gap between
   * what Search lists and what Track can draw is otherwise invisible and reads as a bug.
   *
   * Much smaller than it used to be. A crowded frame was one subjectless row — every person in it
   * unfollowable — and is now one row per person, each with its own track.
   */
  events_without_subject?: number;
  /** Present for aggregate: 'timeline'. */
  bucket?: 'hour' | 'day';
  /**
   * Present for aggregate: 'timeline' — the window the query actually ran against, resolved.
   *
   * The client sends a preset; the server turns it into bounds in the NODE's local time. The
   * timeline strip needs those bounds rather than its own guess at them: it zero-fills across
   * the whole window and returns a sub-range to filter by, and a cloud operator in a different
   * timezone would otherwise brush against a different day than the one that was queried.
   */
  window?: { from: string; to: string };

  // ── by_place ──
  place_by?: SearchPlaceBy;
  /**
   * True for `place_by: 'group'` ONLY, and load-bearing: a camera's group list holds every
   * ancestor, so one camera in Reception credits Reception, Office and the site. Group rows
   * therefore do not sum to the total, and a UI that renders them flat looks like it is
   * double-counting. Render the tree instead.
   */
  overlapping?: boolean;
  /** The group map had not loaded — every camera reported as ungrouped for a reason. */
  group_map_cold?: boolean;
  /** Per-group distinct-subject counts were too expensive here; `subjects` is unreliable. */
  subjects_omitted?: boolean;

  // ── journey ──
  /** Minutes of quiet that separate two visits to the same place. */
  gap_minutes?: number;
  /** Always true: a trail's ends are the query window's, not a turnstile's. Say so. */
  window_bounded?: boolean;
  /** How many subjects' trails came back. */
  subjects?: number;

  // ── attribute_split ──
  split_by?: { field: string; value: string };
  events_with?: number;
  events_without?: number;
  /** The figure to report: how many PEOPLE, not how many detections. */
  subjects_with?: number;
  subjects_without?: number;

  /** One entry per text clause: the spellings each typed word matched. Present with text only. */
  text_matches?: SearchTextReport[];
  /** Per typed word, how it was read — present when the query carried `match`. */
  match_report?: SearchMatchReport[];

  took_ms?: number;
  [key: string]: unknown;
}

/** One selectable value for a chip, with how many events carry it in the current window. */
export interface SearchFacetValue {
  value: string;
  events: number;
}

/**
 * Facets per attribute field. A field present with an EMPTY array is the signal for "not
 * collected on these cameras" — distinct from a field with values none of which match.
 */
export type SearchFacets = Record<string, SearchFacetValue[]>;

/**
 * One searchable SUBJECT — a person or a vehicle — and the filters that apply to it.
 *
 * Served by the API rather than hard-coded in the UI, so adding a field on one subject does not
 * need a matching edit in the frontend to become visible.
 */
export interface SearchSubject {
  label: string;
  /** Model keys that carry this subject's attributes — the OFFERABLE set. */
  models: string[];
  /**
   * The models searched until the operator says otherwise, a subset of `models`.
   *
   * The two differ only for `person`, and that difference is the point: one human walking past a
   * camera running both detectors produces a person event AND a face event, so searching both by
   * default double-counts them. Person events carry the clothing attributes, face events carry
   * the matched identity — neither is redundant, so the choice is made visible rather than
   * decided, and it starts narrow.
   */
  defaultModels?: string[];
  /**
   * The models that carry an IDENTITY for this subject — the only ones a name (or a plate, or
   * "enrolled only") can match.
   *
   * For `person` that is face recognition alone: person-detection events have no identity column,
   * so their projection rows have `subject_label = NULL` and `is_known = false`. A name searched
   * against the narrow default could therefore never match, and answered "nobody" about somebody
   * who was there. A client filtering by identity unions this into the models it sends.
   */
  identityModels?: string[];
  /** Attribute fields, in the order they should be offered. */
  fields: string[];
  /** The free-text identity filter this subject supports: a person name, or a vehicle plate. */
  textFilter?: 'person' | 'plate' | null;
  /** Which text-index fields this subject's fuzzy text search reads. */
  textFields?: SearchTextField[];
}

/** The closed vocabularies and enums the filter builder may offer. */
export interface SearchSchema {
  /** The UNION of every subject's vocabulary, field → allowed values. */
  attributes: Record<string, string[]>;
  /** Which fields belong to which subject. Drives the Person / Vehicle switch. */
  subjects?: Record<string, SearchSubject>;
  models: string[];
  presets: string[];
  aggregates: string[];
  /** What can count as a place. */
  place_by?: SearchPlaceBy[];
  /**
   * Which groupings each place-based aggregate accepts, and its default.
   *
   * Served per aggregate because they differ: `by_place` takes all three, while `journey` refuses
   * `group` — a camera's ancestor list would emit one stop at three levels of the tree. A UI
   * reading this cannot offer a grouping the validator rejects.
   */
  place_aggregates?: Partial<Record<SearchAggregate, { allowed: SearchPlaceBy[]; default: SearchPlaceBy }>>;
  /**
   * Fuzzy text search. Its PRESENCE is the capability: a node without it must be sent
   * `person.name` / `plate` instead of `text`, which it would reject.
   */
  /** The OCR read filter's values. Its PRESENCE is the capability — an older node 400s on it. */
  ocr_read?: Array<'plate' | 'text_only'>;
  /** `or_text` support. Its PRESENCE is the capability — an older node 400s on the field. */
  or_text?: { fields: SearchTextField[]; aggregates: SearchAggregate[] };
  /** The one-box search (`match`, `kinds`, `/refine`). Presence is the capability. */
  match?: { types: SearchEventType[]; refine: boolean; fuzziness?: SearchFuzziness[] };
  text_search?: {
    fields: SearchTextField[];
    fuzziness: SearchFuzziness[];
    default_fuzziness: SearchFuzziness;
    sorts: SearchSort[];
  };
}

/** One registered identity, as the search page needs it: enough to match a name and set a filter. */
export interface PersonRef {
  person_id: string;
  name: string | null;
}

export interface SearchApi {
  /** Run a search. Rejects with an ApiError carrying the server's message on a bad filter. */
  events(dsl: SearchDsl): Promise<SearchResult>;
  /** Which attribute values occur in this window/scope. Takes the same DSL (attributes ignored). */
  facets(dsl: SearchDsl): Promise<SearchFacets>;
  /**
   * What the current results contain, counted per kind, camera, attribute value and watch-list
   * state — the one-box page's Refine panel. Optional: a host without it keeps the old panel.
   */
  refine?(dsl: SearchDsl): Promise<SearchRefine>;
  /** Vocabularies + enums. Cacheable for the session — it describes the schema, not the data. */
  schema(): Promise<SearchSchema>;
  /**
   * A playable recording URL for one camera over a time window — an HLS playlist on both nodes.
   *
   * Built by the adapter rather than the page because the two differ in shape: the local node
   * hits its own backend, while the cloud must node-scope the path and carry an auth token in the
   * query string (an <video>/hls.js request cannot send an Authorization header).
   *
   * Returns null when the host cannot resolve one — the cloud with no node selected, say — and
   * the Playback control is then not offered rather than offered and broken.
   */
  playbackUrl(cameraId: string, fromIso: string, toIso: string): string | null;
  /**
   * The bearer token for playback requests, read fresh on every call.
   *
   * hls.js fetches playlists and segments with its own XHR, so nothing passes through the app's
   * axios interceptor and no Authorization header is attached — the request comes back 401
   * "Missing or invalid authorization header". Lives on the adapter because the two dashboards
   * store the token under different keys, and must be a CALL rather than a value because the
   * token silently rotates (~30 min): one captured at player setup goes stale mid-session.
   *
   * Return null where the transport carries auth another way (a token already in the URL).
   */
  playbackAuthToken?(): string | null;
  /**
   * Cameras and groups the requester may pick, for the scope chips.
   *
   * Declared here rather than borrowed from ModelApi/AccessControlApi, following this file's
   * existing convention that each domain owns its own lookups — the cloud binds the selected
   * node into its implementation, and sharing another domain's would tie the two together.
   */
  listCameras(): Promise<CameraRef[]>;
  listGroups(): Promise<Group[]>;
  /**
   * The registered people, so a typed sentence can name one — "where was Asha today".
   *
   * A name cannot be parsed out of free text the way a colour can: "asha" is not in any
   * vocabulary, and guessing that an unrecognised word is a person would turn every typo into a
   * name filter that matches nobody. So the registry is the closed list, exactly as the camera
   * and group names are for places.
   *
   * OPTIONAL, and it fails soft. Listing the registry needs `ai_events.view`, which a user with
   * search access may not have; the name box still works (the filter is applied server-side
   * against the event rows, not against the registry), only the typed-sentence shortcut is off.
   */
  listPeople?(): Promise<PersonRef[]>;
}

// ─── Vehicle watchlist ──────────────────────────────────────────────────────

/**
 * One blacklisted vehicle.
 *
 * `plate_number` is the CANONICAL form the node matches on — uppercased, separators stripped —
 * and `plate_display` is what the operator typed. Show the display one; send either, since the
 * node normalises whatever it is given.
 */
export interface VehicleBlacklistEntry {
  id: string;
  plate_number: string;
  plate_display: string | null;
  reason: string | null;
  category: string | null;
  notes: string | null;
  /** Severity of the alert a sighting raises. 'critical' unless the operator lowered it. */
  severity: 'critical' | 'warning' | 'normal' | string;
  is_active: boolean;
  /** ISO timestamp, or null for a permanent listing. */
  expires_at: string | null;
  added_by: string | null;
  created_at: string;
  updated_at: string;
}

/** Fields an operator may set. Everything but the plate is optional. */
export interface VehicleBlacklistInput {
  plate_number: string;
  plate_display?: string;
  reason?: string | null;
  category?: string | null;
  notes?: string | null;
  severity?: string;
  is_active?: boolean;
  expires_at?: string | null;
  added_by?: string | null;
}

export interface VehicleBlacklistListParams {
  search?: string;
  category?: string;
  is_active?: boolean | string;
  page?: number;
  limit?: number;
}

export interface VehicleBlacklistPage {
  data: VehicleBlacklistEntry[];
  pagination?: { page: number; limit: number; total: number; total_pages: number };
}

export interface VehicleBlacklistApi {
  list(params?: VehicleBlacklistListParams): Promise<VehicleBlacklistPage>;
  /** Add, or re-list a plate that was removed — the node upserts on the plate. */
  add(input: VehicleBlacklistInput): Promise<VehicleBlacklistEntry>;
  update(id: string, patch: Partial<VehicleBlacklistInput>): Promise<VehicleBlacklistEntry>;
  remove(id: string): Promise<void>;
  /** Is this plate listed? Used to warn before adding a duplicate. */
  check(plate: string): Promise<{ listed: boolean; entry: VehicleBlacklistEntry | null }>;
}

// ─── Watch list ─────────────────────────────────────────────────────────────

/**
 * A person on the watch list.
 *
 * Flagging is COLUMNS ON `persons`, not a list beside it (migration 054): a face can only be
 * recognised if the person is already registered, so every listed person is by definition in
 * the registry. That is why this looks like a person record with watchlist fields rather than
 * a watchlist entry with a person attached — the vehicle side is the other way round, because
 * a plate needs no prior registration to be watched for.
 */
export interface WatchlistPerson {
  person_id: string;
  name: string | null;
  label?: string | null;
  is_blacklisted: boolean;
  /** blacklisted | wanted | missing — names the alert a sighting raises. */
  blacklist_category: string | null;
  blacklist_reason: string | null;
  blacklist_severity: string | null;
  /** False = flagged but SILENT: visible on the list, raises no alert. */
  blacklist_alert?: boolean;
  blacklisted_at?: string | null;
  blacklisted_by?: string | null;
  photo_count?: number;
  primary_photo_id?: string | null;
  [key: string]: unknown;
}

/** What flagging a person sets. Matches the register dialogs' `PersonWatchlistValue`. */
export interface WatchlistPersonInput {
  reason?: string | null;
  category?: string;
  severity?: string;
  alert?: boolean;
}

/**
 * One SIGHTING of a watchlisted subject, people and vehicles in one shape.
 *
 * Read from the event's own verdict, recorded at ingest — not by re-matching against today's
 * list. De-listing a subject must not erase the sightings that justified listing them, so
 * `category` / `reason` (which come from the list) can be null on a sighting that still
 * stands.
 */
export interface WatchlistDetection {
  kind: 'person' | 'vehicle';
  /**
   * The report model the sighting's event lives in — `face`, `vehicle_crossing`, or `ocr` for a
   * listed plate read by OCR. A vehicle is no longer always a crossing, so the record must be
   * opened by this, not guessed from `kind`. Optional: nodes older than migration 059 omit it.
   */
  model?: string;
  event_id: string;
  camera_id: string;
  /** Epoch seconds, like every other event timestamp in this API. */
  event_timestamp: number;
  snapshot_path: string | null;
  /**
   * The event's object-store image map — `{ fullFrame, faceCrop, … }`, whatever the AI module
   * named them. This is the one a browser can load; `image_paths` below is the legacy
   * disk-relative map, present only on rows written before migration 020.
   *
   * Both are optional: a sighting is a sighting whether or not a frame survived retention, and
   * the feed draws its letter avatar rather than a gap.
   */
  image_urls?: Record<string, string> | null;
  image_paths?: Record<string, string> | null;
  /** The person id, or the canonical plate. */
  subject_id: string | null;
  /** The name, or the plate as the operator typed it. */
  subject_label: string | null;
  category: string | null;
  reason: string | null;
  severity: string | null;
  confidence: number | null;
  /** entry | exit, vehicles only. */
  direction: string | null;
}

export interface WatchlistDetectionParams {
  subject?: 'person' | 'vehicle';
  /** Epoch seconds. */
  from?: number;
  to?: number;
  camera_id?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export interface WatchlistDetectionPage {
  data: WatchlistDetection[];
  pagination?: { page: number; limit: number; total: number; total_pages: number };
}

export interface WatchlistPeopleParams {
  search?: string;
  blacklist_category?: string;
  page?: number;
  limit?: number;
}

export interface WatchlistPeoplePage {
  data: WatchlistPerson[];
  pagination?: { page: number; limit: number; total: number; total_pages: number };
}

/**
 * The watch list's PEOPLE half plus the sightings feed.
 *
 * Vehicles are absent on purpose: `VehicleBlacklistApi` already covers them end to end, and a
 * second way to reach the same endpoints would be a second thing to keep in step.
 */
export interface WatchlistApi {
  /** Listed people only — the endpoint filters `is_blacklisted`. */
  people(params?: WatchlistPeopleParams): Promise<WatchlistPeoplePage>;
  /** Everyone in the registry, for choosing somebody to add. */
  searchRegistry(params?: { search?: string; limit?: number }): Promise<WatchlistPeoplePage>;
  flagPerson(personId: string, input: WatchlistPersonInput): Promise<WatchlistPerson | void>;
  unflagPerson(personId: string): Promise<void>;
  detections(params?: WatchlistDetectionParams): Promise<WatchlistDetectionPage>;
  detectionSummary(params?: Omit<WatchlistDetectionParams, 'subject' | 'page' | 'limit'>):
    Promise<{ person: number; vehicle: number; total: number }>;
}

/**
 * Bulk camera import — the format, and the upload.
 *
 * WHY IT IS AN INJECTED SLICE. The two apps reach the same importer by different roads: the
 * local app POSTs multipart straight at its own backend, while the cloud reads the file in the
 * browser and sends it base64 through the node tunnel, which is a JSON channel multipart cannot
 * cross. Identical screen, identical verdicts, two transports — which is exactly what this seam
 * is for.
 */
export interface CameraImportColumn {
  name: string;
  required: boolean;
  max: number;
  description: string;
  example: string;
}

export interface CameraImportSpec {
  columns: CameraImportColumn[];
  required_columns: string[];
  max_rows: number;
  group_separator: string;
  notes: string[];
  template_csv: string;
}

export interface CameraImportRowResult {
  line: number;
  name: string;
  status: 'created' | 'skipped' | 'failed';
  camera_id?: string;
  reason?: string;
}

export interface CameraImportSummary {
  total: number;
  created: number;
  skipped: number;
  failed: number;
  ignored_columns: string[];
  format?: 'csv' | 'xlsx';
  sheet?: string;
}

/**
 * The outcome of one upload. NEVER THROWN — a file full of bad rows is the normal, expected
 * result of this screen, and its per-row reasons are the answer rather than an error.
 */
export interface CameraImportOutcome {
  ok: boolean;
  message: string;
  summary: CameraImportSummary;
  rows: CameraImportRowResult[];
  /** Whole-file refusals (no header, missing column, too many rows) carry these instead of rows. */
  details: Array<{ field?: string; message: string }>;
}

export interface CameraImportApi {
  /** The column contract, served by the node so the page and the parser cannot disagree. */
  spec(): Promise<CameraImportSpec>;
  upload(file: File, onProgress?: (percent: number) => void): Promise<CameraImportOutcome>;
}

/**
 * One stored recording segment, as both nodes' `/recordings/segments` return it (the cloud's
 * is the local one, tunnelled). Only the fields the offset table and the timeline read.
 */
export interface RecordingSegment {
  id: string | number;
  start_time: string;
  end_time: string;
  /** 'finalized' | 'in-progress'. Missing means finalized — older rows predate the column. */
  status?: string;
  /** Seconds the unified PLAYLIST gives this segment — what player-time advances by. */
  playlist_duration?: number;
  duration?: number;
  /** Why it was kept: 'continuous' | 'event' | 'motion' | 'manual'. */
  trigger?: string;
  [key: string]: unknown;
}

/**
 * A retention window from `recording_triggers` — why event/motion footage exists. One row per
 * window (merged triggers bump `trigger_count`), not per detection.
 */
export interface RecordingTriggerWindow {
  id?: string | number;
  /** 'ai_event' | 'motion' | 'manual' | 'api'. */
  source?: string;
  /** Detection category for ai_event (person, intrusion, …); null otherwise. */
  event_type?: string | null;
  event_time: string;
  window_start?: string;
  window_end?: string;
  trigger_count?: number;
  [key: string]: unknown;
}

/**
 * Wall playback — the Live Wall's Recorded mode: up to six cameras playing the same moment.
 *
 * Everything it needs already exists on both nodes (segments + one seekable playlist per camera
 * and range), so this is the recordings surface the page reads, not a new backend. The cloud
 * binds the selected node, exactly like the other node-scoped adapters.
 */
export interface WallPlaybackApi {
  listCameras(): Promise<CameraRef[]>;
  /** Flat, with parent_id — the camera panel rebuilds the tree. */
  listGroups(): Promise<Group[]>;
  /** Segments overlapping [fromIso, toIso], any order. */
  getSegments(cameraId: string, fromIso: string, toIso: string): Promise<RecordingSegment[]>;
  /** Days with footage, as local `YYYY-MM-DD`. Best-effort: [] when the node cannot say. */
  getAvailableDates(cameraId: string): Promise<string[]>;
  /**
   * Event / motion retention windows overlapping [fromIso, toIso] — the timeline colours them.
   * Best-effort: [] when the node has none or cannot say; playback never depends on it.
   */
  getTriggers(cameraId: string, fromIso: string, toIso: string): Promise<RecordingTriggerWindow[]>;
  /** The unified HLS playlist for one camera over the window. Null = cannot build one. */
  playbackUrl(cameraId: string, fromIso: string, toIso: string): string | null;
  /** Bearer for hls.js' own requests, read per request — see SearchApi.playbackAuthToken. */
  playbackAuthToken(): string | null;
  /** The stream crosses the cloud's WebSocket tunnel → the player's longer timeouts. */
  tunnelled: boolean;
}

export interface TrinetraApi {
  roles: RoleApi;
  users: UserApi;
  groups: GroupApi;
  models: ModelApi;
  discovery: DiscoveryApi;
  hexAlerts: HexAlertsApi;
  mapLayouts: MapLayoutApi;
  geoMap: GeoMapApi;
  accessControl: AccessControlApi;
  attendance: AttendanceApi;
  heatmap: HeatmapApi;
  search: SearchApi;
  vehicleBlacklist: VehicleBlacklistApi;
  watchlist: WatchlistApi;
  wallPlayback: WallPlaybackApi;
  /**
   * Optional: an app without a camera importer simply omits it, and the page is not routed
   * there. Both apps supply it today.
   */
  cameraImport?: CameraImportApi;
}
